import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, KeyRound } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useSecurityMeta } from "@/components/useSecurityMeta";

const inputClass =
  "w-full rounded-xl border border-choco-line bg-choco px-4 py-2.5 pr-11 text-sm text-cream-text placeholder:text-cream-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-focus";

const PasswordField = ({ id, label, value, onChange, show, onToggle, testid, toggleTestid, error, describedBy }) => (
  <div>
    <label htmlFor={id} className="mb-1.5 block text-sm font-semibold text-cream-text">{label}</label>
    <div className="relative">
      <input
        id={id}
        type={show ? "text" : "password"}
        required
        autoComplete="new-password"
        value={value}
        onChange={onChange}
        aria-invalid={Boolean(error)}
        aria-describedby={describedBy}
        className={inputClass}
        data-testid={testid}
      />
      <button
        type="button"
        onClick={onToggle}
        aria-label={show ? `Hide ${label.toLowerCase()}` : `Show ${label.toLowerCase()}`}
        aria-pressed={show}
        className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1.5 text-cream-muted hover:text-gold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-focus"
        data-testid={toggleTestid}
      >
        {show ? <EyeOff className="h-4 w-4" aria-hidden="true" /> : <Eye className="h-4 w-4" aria-hidden="true" />}
      </button>
    </div>
  </div>
);

export default function AdminResetPassword() {
  useSecurityMeta();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState("");
  const [linkDead, setLinkDead] = useState(false);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 10) {
      setError("Your new password must be at least 10 characters long.");
      return;
    }
    if (pw !== confirm) {
      setError("The two passwords don't match — please re-enter them.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/auth/reset-password", { token, new_password: pw });
      setDone(true);
    } catch (err) {
      const status = err.response?.status;
      if (status === 400 || status === 422) {
        setLinkDead(true);
      } else {
        setError(formatApiError(err));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-choco px-4" data-testid="reset-password-page">
      <div className="w-full max-w-sm rounded-3xl border border-choco-line bg-choco-light p-8">
        <img src="/assets/logo.png" alt="Bakes by CR" className="h-16 w-auto" />
        <h1 className="mt-4 font-serif text-2xl font-bold text-cream-text">Choose a new password</h1>

        {done ? (
          <div className="mt-4" data-testid="reset-success">
            <KeyRound className="h-8 w-8 text-gold" aria-hidden="true" />
            <p className="mt-3 text-sm leading-relaxed text-cream-text" role="status">
              Your password has been changed. For your security, every other sign-in has been logged out.
              Please sign in with your new password.
            </p>
            <button
              type="button"
              onClick={() => navigate("/admin/login")}
              className="mt-6 w-full rounded-full bg-gold px-6 py-3 text-sm font-bold text-choco transition-colors hover:bg-gold-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-focus"
              data-testid="reset-go-to-login"
            >
              Go to sign in
            </button>
          </div>
        ) : linkDead || !token ? (
          <div className="mt-4" data-testid="reset-link-invalid">
            <p className="text-sm leading-relaxed text-cream-text" role="alert">
              This reset link is invalid, has already been used, or has expired. Reset links work once and expire after 60 minutes.
            </p>
            <button
              type="button"
              onClick={() => navigate("/admin/forgot-password")}
              className="mt-6 w-full rounded-full bg-gold px-6 py-3 text-sm font-bold text-choco transition-colors hover:bg-gold-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-focus"
              data-testid="reset-request-new-link"
            >
              Request a new reset link
            </button>
            <Link to="/admin/login" className="mt-4 block text-center text-xs font-semibold text-cream-muted hover:text-gold" data-testid="reset-back-to-login">
              Back to sign in
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} noValidate data-testid="reset-password-form">
            <p id="pw-requirements" className="mt-1 text-sm leading-relaxed text-cream-muted">
              At least 10 characters. Spaces and password-manager-generated passwords are welcome — no symbols are forced.
            </p>
            <div className="mt-6 space-y-4">
              <PasswordField id="reset-pw" label="New password" value={pw} onChange={(e) => setPw(e.target.value)}
                show={showPw} onToggle={() => setShowPw((s) => !s)} testid="reset-new-password-input"
                toggleTestid="reset-toggle-password" error={error} describedBy="pw-requirements" />
              <PasswordField id="reset-confirm" label="Confirm new password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                show={showConfirm} onToggle={() => setShowConfirm((s) => !s)} testid="reset-confirm-password-input"
                toggleTestid="reset-toggle-confirm" error={error} />
            </div>
            {error && (
              <p className="mt-4 text-sm font-semibold text-red-400" role="alert" data-testid="reset-error">{error}</p>
            )}
            <button
              type="submit"
              disabled={busy || !pw || !confirm}
              className="mt-6 w-full rounded-full bg-gold px-6 py-3 text-sm font-bold text-choco transition-colors hover:bg-gold-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-focus active:scale-[0.98] disabled:opacity-60"
              data-testid="reset-submit-button"
            >
              {busy ? "Updating…" : "Set new password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
